import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';
import { AxiosError } from 'axios';

// Define the state interface
interface ReferralState {
  referralLink: string | null;
  referralCode: string | null;
  isLoading: boolean;
  error: string | null;
  referrals: ReferralUser[];
  referralStats: ReferralStats | null;
}

// Interface for referred users
interface ReferralUser {
  id: number;
  name: string;
  email: string;
  registered_at: string;
  status: 'pending' | 'active';
}



// Interface for referral statistics
interface ReferralStats {
  total_referrals: number;
  successful_referrals: number;
  pending_referrals: number;
  rewards_earned: number;
}

// Initial state
const initialState: ReferralState = {
  referralLink: null,
  referralCode: null,
  isLoading: false,
  error: null,
  referrals: [],
  referralStats: null
};

// Async thunk to generate/fetch a referral link
export const generateReferralLink = createAsyncThunk(
  'referral/generateReferralLink',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/refferal/link');
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

// Async thunk to fetch referred users
export const fetchReferrals = createAsyncThunk(
  'referral/fetchReferrals',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/refferal/list');
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

// Async thunk to fetch referral statistics
export const fetchReferralStats = createAsyncThunk(
  'referral/fetchReferralStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/refferal/stats');
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

// Create the referral slice
const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    clearReferralErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle generateReferralLink
      .addCase(generateReferralLink.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(generateReferralLink.fulfilled, (state, action) => {
        state.isLoading = false;
        state.referralLink = action.payload.referral_link;
        state.referralCode = action.payload.referral_code;
      })
      .addCase(generateReferralLink.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as any)?.message || 'Failed to generate referral link';
      })
      
      // Handle fetchReferrals
      .addCase(fetchReferrals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReferrals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.referrals = action.payload.referrals;
      })
      .addCase(fetchReferrals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as any)?.message || 'Failed to fetch referrals';
      })
      
      // Handle fetchReferralStats
      .addCase(fetchReferralStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReferralStats.fulfilled, (state, action) => {
        state.isLoading = false;
        console.log(action.payload.statistics)
        state.referralStats = action.payload.statistics;
        console.log(state.referralStats);
      })
      .addCase(fetchReferralStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as any)?.message || 'Failed to fetch referral statistics';
      });
  },
});

// Export actions
export const { clearReferralErrors } = referralSlice.actions;

// Export reducer
export default referralSlice.reducer; 